import React, { useState } from 'react';
import { IonPage, IonContent, useIonRouter } from '@ionic/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForgotPassword } from '@/hooks/useAuth';
import '@/theme/login.css';

const schema = z.object({
  email: z
    .string()
    .min(1, 'Email-ul este obligatoriu')
    .email('Introduceți un email valid'),
});

type FormData = z.infer<typeof schema>;

const ForgotPasswordPage: React.FC = () => {
  const router = useIonRouter();
  const mutation = useForgotPassword();
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const emailValue = watch('email');

  const onSubmit = async (data: FormData) => {
    try {
      await mutation.mutateAsync(data.email);
    } catch {
      // Show success regardless to prevent email enumeration
    }
    setSubmitted(true);
  };

  const isPending = isSubmitting || mutation.isPending;

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false}>
        <div className="login-split">
          {/* ── Left: Form Panel ── */}
          <div className="login-form-panel">
            <div className="login-form-inner">
              {submitted ? (
                /* ── Success State ── */
                <div className="login-animate login-animate--1">
                  <div className="login-header">
                    <h1 className="login-title">
                      Verificați email-ul.
                    </h1>
                    <p className="login-subtitle">
                      Dacă există un cont cu acest email, am trimis un link de resetare.
                      Verificați inbox-ul și folderul spam.
                    </p>
                  </div>
                  <div className="login-actions">
                    <button
                      type="button"
                      className="login-btn login-btn--primary"
                      onClick={() => router.push('/login', 'back')}
                    >
                      Înapoi la autentificare
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="login-header login-animate login-animate--1">
                    <h1 className="login-title">
                      Ați uitat parola?
                      <br />
                      Introduceți datele pentru recuperare.
                    </h1>
                    <p className="login-subtitle">
                      Introduceți adresa de email pentru a primi un link de resetare
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className="login-fields">
                      <div
                        className={`login-field login-animate login-animate--2${
                          emailValue ? ' login-field--has-value' : ''
                        }`}
                      >
                        <input
                          {...register('email')}
                          type="email"
                          className="login-field__input"
                          placeholder=" "
                          autoComplete="email"
                          autoFocus
                        />
                        <label className="login-field__label">Email</label>
                        <span className="login-field__icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="4" width="20" height="16" rx="2" />
                            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                          </svg>
                        </span>
                        {errors.email && (
                          <span className="login-field__error">{errors.email.message}</span>
                        )}
                      </div>
                    </div>

                    <div className="login-actions login-animate login-animate--3">
                      <button
                        type="submit"
                        className="login-btn login-btn--primary"
                        disabled={isPending}
                      >
                        {isPending && <span className="login-spinner" />}
                        Recuperare
                      </button>
                    </div>
                  </form>

                  {/* Back to login */}
                  <div style={{ textAlign: 'center', marginTop: 32 }} className="login-animate login-animate--4">
                    <a
                      className="login-forgot"
                      href="/login"
                      onClick={(e) => {
                        e.preventDefault();
                        router.push('/login', 'back');
                      }}
                      style={{ textAlign: 'center', display: 'inline' }}
                    >
                      Înapoi la autentificare
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Right: Brand Panel ── */}
          <div className="login-brand-panel">
            <div className="login-brand-panel__shapes">
              <div className="login-decor login-decor--1" />
              <div className="login-decor login-decor--2" />
              <div className="login-decor login-decor--3" />
              <div className="login-decor login-decor--4" />
              <div className="login-decor login-decor--5" />
            </div>
            <div className="login-brand-content">
              <div className="login-brand-logo">
                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h2 className="login-brand-name">Betel Gestiune</h2>
              <p className="login-brand-tagline">
                Platforma completă pentru administrarea eficientă a comunității bisericii
              </p>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ForgotPasswordPage;
